import React from 'react';
import Image from 'next/image';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import { Flex } from '@radix-ui/themes';
import cn from 'classnames';

interface Props {
  src: string | StaticImport;
}

type Point = {
  x: number;
  y: number;
}

const ZoomableImage = ({ src }: Props) => {
  // Position of image 
  const [imageCorners, setImageCorners] = React.useState<{ tl: Point, br: Point }>({
    tl: { x: 0, y: 0 },
    br: { x: 0, y: 0 }
  });
  const [position, setPosition] = React.useState<Point>({ x: 250, y: 250 });

  const [zoomed, setZoomed] = React.useState(false);
  const [lensStyle, setLensStyle] = React.useState({});

  const lensSize = 150; // Size of the lens
  const zoomFactor = 1.4;

  const refreshLens = (e: any) => {
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const trueWidth = imageCorners.br.x - imageCorners.tl.x;
    const trueHeight = imageCorners.br.y - imageCorners.tl.y;
    const Xpadding = (width - trueWidth) / 2;
    const Ypadding = (height - trueHeight) / 2;

    // Coordinates of cursor within container
    const mouseX = e.pageX - left - window.scrollX;
    const mouseY = e.pageY - top - window.scrollY;

    // Update lens position and background
    const bgSize = trueWidth * zoomFactor + 'px ' + trueHeight * zoomFactor + 'px'; // Magnification scale
    const bgPosition = `${-((mouseX - Xpadding) * zoomFactor - lensSize / 2)}px ${-((mouseY - Ypadding) * zoomFactor - lensSize / 2)}px`;

    setPosition({ x: mouseX, y: mouseY });
    setLensStyle({
      backgroundSize: bgSize,
      backgroundPosition: bgPosition,
    })
  };

  const handleMouseMove = refreshLens;
  const handleMouseEnter = () => setZoomed(true);
  const handleMouseLeave = () => setZoomed(false);

  const handleImageLoad = (e: any) => {
    const { width, height } = e.target.getBoundingClientRect();
    const corners = getCorners({
      width: width as number,
      height: height as number,
      naturalWidth: e.target.naturalWidth,
      naturalHeight: e.target.naturalHeight,
    })
    setImageCorners(corners);
  }

  return (
    <Flex
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="h-full w-full select-none hover:cursor-none"
    >
      <Flex
        className={cn(
          "bg-black z-10 w-full h-full transition-colors",
          zoomed ? "bg-opacity-40 z-10" : "bg-opacity-0"
        )}
        style={{ pointerEvents: "none" }}
      />
      <Image
        src={src}
        fill
        alt="Image preview for this lot"
        quality={85}
        priority
        unoptimized
        className="select-none absolute undraggable"
        style={{ objectFit: "contain" }}
        onMouseMove={handleMouseMove}
        onLoad={handleImageLoad}
      />
      {zoomed && (
        <Flex
          id="lens"
          className="bg-white z-20"
          style={{
            position: "absolute",
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: `${lensSize}px`,
            height: `${lensSize}px`,
            transform: "translate(-50%, -50%)",
            border: "1px solid #fff",
            pointerEvents: "none",
          }}
          onClick={() => setZoomed(false)}
        >
          <Flex
            className="w-full h-full"
            style={{
              background: `url(${src}) no-repeat`,
              ...lensStyle,
            }}
          />
        </Flex>
      )}
    </Flex>
  );
}


const getCorners = (
  { width, height, naturalWidth, naturalHeight }:
    { width: number, height: number, naturalWidth: number, naturalHeight: number }
) => {
  const scaleX = width / naturalWidth;
  const scaleY = height / naturalHeight;
  const scale = Math.min(scaleX, scaleY);
  const trueWidth = naturalWidth * scale;
  const trueHeight = naturalHeight * scale;

  const Xpadding = (width - trueWidth) / 2;
  const Ypadding = (height - trueHeight) / 2;

  const tl: Point = {
    x: Xpadding,
    y: Ypadding,
  };
  const br: Point = {
    x: width - Xpadding,
    y: height - Ypadding,
  }

  return { tl, br };
}

export default ZoomableImage
